import React from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { SelectButton } from "primereact/selectbutton";
import type { ThemeMode, ThemeStyle } from "../styles/themeManager";
import { THEME_MODES, THEME_STYLES } from "../styles/themeManager";

const WORLD_CURRENCIES = [
  { label: "US Dollar (USD)", value: "USD" },
  { label: "Euro (EUR)", value: "EUR" },
  { label: "British Pound (GBP)", value: "GBP" },
  { label: "Japanese Yen (JPY)", value: "JPY" },
  { label: "Swiss Franc (CHF)", value: "CHF" },
  { label: "Canadian Dollar (CAD)", value: "CAD" },
  { label: "Australian Dollar (AUD)", value: "AUD" },
  { label: "Chinese Yuan (CNY)", value: "CNY" },
  { label: "Swedish Krona (SEK)", value: "SEK" },
  { label: "Norwegian Krone (NOK)", value: "NOK" },
  { label: "Danish Krone (DKK)", value: "DKK" },
  { label: "Singapore Dollar (SGD)", value: "SGD" },
  { label: "Hong Kong Dollar (HKD)", value: "HKD" },
  { label: "South Korean Won (KRW)", value: "KRW" },
  { label: "Indian Rupee (INR)", value: "INR" },
  { label: "Brazilian Real (BRL)", value: "BRL" },
  { label: "Mexican Peso (MXN)", value: "MXN" },
  { label: "South African Rand (ZAR)", value: "ZAR" },
  { label: "Russian Ruble (RUB)", value: "RUB" },
  { label: "Turkish Lira (TRY)", value: "TRY" },
  { label: "Polish Zloty (PLN)", value: "PLN" },
  { label: "Thai Baht (THB)", value: "THB" },
  { label: "New Zealand Dollar (NZD)", value: "NZD" },
  { label: "Israeli Shekel (ILS)", value: "ILS" },
  { label: "Czech Koruna (CZK)", value: "CZK" },
].sort((a, b) => a.label.localeCompare(b.label));

const AI_MODELS = [
  { label: "GPT-4o Mini (Fast & Affordable)", value: "gpt-4o-mini" },
  { label: "GPT-4o (Highest Quality)", value: "gpt-4o" },
  {
    label: "Claude 3.5 Haiku (Fast & Cheap)",
    value: "claude-3-5-haiku-20241022",
  },
  {
    label: "Claude 3.5 Sonnet (Best Reasoning)",
    value: "claude-3-5-sonnet-20241022",
  },
  {
    label: "Gemini 3 Pro (Preview)",
    value: "gemini-3-pro-preview",
  },
];

const DEFAULT_NODE_TYPES = [
  { label: "Card (Standard Rectangle)", value: "nodeItem" },
  { label: "Note (Circle)", value: "noteItem" },
];

interface UserSettingsProps {
  visible: boolean;
  onHide: () => void;
  headerElement: React.ReactNode;
  footerContent: React.ReactNode;
  currency: string;
  setCurrency: (value: string) => void;
  showWhyTag: boolean;
  setShowWhyTag: (value: boolean) => void;
  showStartupDialog: boolean;
  setShowStartupDialog: (value: boolean) => void;
  showAIPromptOnNewMap: boolean;
  setShowAIPromptOnNewMap: (value: boolean) => void;
  showGettingStarted: boolean;
  setShowGettingStarted: (value: boolean) => void;
  aiModel: string;
  setAiModel: (value: string) => void;
  autoSave: boolean;
  setAutoSave: (value: boolean) => void;
  themeMode: ThemeMode;
  setThemeMode: (value: ThemeMode) => void;
  themeStyle: ThemeStyle;
  setThemeStyle: (value: ThemeStyle) => void;
  defaultNodeType: string;
  setDefaultNodeType: (value: string) => void;
  edgeType: string;
  setEdgeType: (value: string) => void;
}

const UserSettings: React.FC<UserSettingsProps> = ({
  visible,
  onHide,
  headerElement,
  footerContent,
  currency,
  setCurrency,
  showWhyTag,
  setShowWhyTag,
  showStartupDialog,
  setShowStartupDialog,
  showAIPromptOnNewMap,
  setShowAIPromptOnNewMap,
  showGettingStarted,
  setShowGettingStarted,
  aiModel,
  setAiModel,
  autoSave,
  setAutoSave,
  themeMode,
  setThemeMode,
  themeStyle,
  setThemeStyle,
  defaultNodeType,
  setDefaultNodeType,
  edgeType,
  setEdgeType,
}) => {
  return (
    <Dialog
      visible={visible}
      modal
      header={headerElement}
      footer={footerContent}
      className="user-settings-dialog"
      onHide={onHide}
    >
      <TabView className="user-settings-p-tabview-nav">
        <TabPanel header="General">
          <div className="user-settings-section">
            <div className="user-settings-row">
              <label htmlFor="currency" className="user-settings-label">
                Currency:
              </label>
              <Dropdown
                className="currency-dropdown user-settings-dropdown"
                id="currency"
                value={currency}
                options={WORLD_CURRENCIES}
                onChange={(e) => setCurrency(e.value)}
                placeholder="Select a currency"
                filter
              />
            </div>

            <div className="user-settings-row">
              <div className="user-settings-switch-row">
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showWhyTag"
                    checked={showWhyTag}
                    onChange={(e) => setShowWhyTag(e.value)}
                  />
                </div>
                <span className="user-settings-switch-text">
                  Display the "Why?" tag on the Goal node
                </span>
              </div>
            </div>

            <div className="user-settings-row">
              <div className="user-settings-switch-row">
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showStartupDialog"
                    checked={showStartupDialog}
                    onChange={(e) => setShowStartupDialog(e.value)}
                  />
                </div>
                <span className="user-settings-switch-text">
                  Show 'Guided Creation Flow' when starting a new map
                </span>
              </div>
            </div>

            <div className="user-settings-row">
              <div className="user-settings-switch-row">
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showAIPromptOnNewMap"
                    checked={showAIPromptOnNewMap}
                    onChange={(e) => setShowAIPromptOnNewMap(e.value)}
                  />
                </div>
                <span className="user-settings-switch-text">
                  Show AI prompt window when creating a new map
                </span>
              </div>
            </div>

            <div className="user-settings-row">
              <div className="user-settings-switch-row">
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showGettingStarted"
                    checked={showGettingStarted}
                    onChange={(e) => setShowGettingStarted(e.value)}
                  />
                </div>
                <span className="user-settings-switch-text">
                  Show 'Getting Started' video on new map creation
                </span>
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel header="Appearance">
          <div className="user-settings-section">
            <div className="user-settings-row">
              <label htmlFor="themeMode" className="user-settings-label">
                Theme Mode:
              </label>
              <SelectButton
                id="themeMode"
                value={themeMode}
                options={[...THEME_MODES]}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setThemeMode(e.value)}
                className="user-settings-theme-mode-buttons"
              />
            </div>

            <div className="user-settings-row">
              <label htmlFor="themeStyle" className="user-settings-label">
                Theme Style:
              </label>
              <Dropdown
                id="themeStyle"
                value={themeStyle}
                options={[...THEME_STYLES]}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setThemeStyle(e.value)}
                placeholder="Select a theme style"
                className="user-settings-dropdown"
              />
            </div>

            <div className="user-settings-row">
              <label htmlFor="defaultNodeType" className="user-settings-label">
                Default Node Type:
              </label>
              <Dropdown
                className="user-settings-dropdown"
                id="defaultNodeType"
                value={defaultNodeType}
                options={DEFAULT_NODE_TYPES}
                onChange={(e) => setDefaultNodeType(e.value)}
                placeholder="Select default node type"
              />
            </div>

            <div className="user-settings-row">
              <label htmlFor="edgeType" className="user-settings-label">
                Edge Style:
              </label>
              <Dropdown
                id="edgeType"
                value={edgeType}
                options={[
                  { label: "Curved (Bezier)", value: "bezier" },
                  { label: "Straight (Mindmap)", value: "straight" },
                ]}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setEdgeType(e.value)}
                placeholder="Select edge style"
                className="user-settings-dropdown"
              />
            </div>
          </div>
        </TabPanel>
        <TabPanel header="Data">
          <div className="user-settings-section">
            <div className="user-settings-row">
              <div className="user-settings-switch-row">
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="autoSave"
                    checked={autoSave}
                    onChange={(e) => setAutoSave(e.value)}
                  />
                </div>
                <span className="user-settings-switch-text">
                  Enable automatic background saving
                </span>
              </div>
            </div>

            <div className="user-settings-desc">
              <p className="user-settings-desc-paragraph">
                When enabled, your map will be automatically saved in the
                background 3 seconds after each change, without interrupting
                your workflow.
              </p>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default UserSettings;
